// Read-time workout-calorie RESOLVER (HC-5 Slice B3). Pure, IO-free. The
// counterpart to the write-path estimator (sessionCalorieEstimate.ts): the
// estimator COMPUTES a number at save; this PICKS which number is canonical at
// read. A session can carry TWO calorie figures — the measured value from
// Health Connect (`session.calories`) and JL's own estimate
// (`session.caloriesEstimate`) — persisted side by side, never overwriting each
// other (the body-comp dual-curve reasoning). Resolving at READ (not baking a
// winner at write) means flipping the "HC wins" preference never rewrites a
// stored row.
//
// Precedence (next/health-connect-cgm.md §HC-5, decision 2026-07-08):
//   • HC value absent            → the JL estimate wins (it's the only figure).
//   • HC present + hcWins ON      → HC wins; the estimate is retained as the
//                                   secondary reading.
//   • HC present + hcWins OFF     → the JL estimate wins; HC retained secondary.
//   • neither present            → null (nothing to show).
// hcWins is passed in explicitly — the default-ON preference lives on the client
// (hcCaloriesPreference), read by the caller. This module stays a pure picker.

import type { CaloriesEstimate } from "./schemas/session.js";

/** Which of the two calorie figures the resolver picked as canonical. `hc` = the
 *  measured `session.calories` (Health-Connect-enriched in practice); `jl:estimate`
 *  = JL's own computed estimate. Mirrors the `source` tag on body-comp curves. */
export type WorkoutCalorieSource = "hc" | "jl:estimate";

export interface ResolvedWorkoutCalories {
  /** The canonical kcal to surface, chosen per `hcWins`. */
  kcal: number;
  /** Which figure won. */
  source: WorkoutCalorieSource;
  /** The measured HC/manual value, when present — retained as the secondary
   *  reading even when the estimate wins (so the UI can show both). */
  measured?: number;
  /** JL's own estimate, when present — retained as secondary even when HC wins. */
  estimate?: CaloriesEstimate;
}

/** Pick the canonical workout-calorie figure at read time. Returns `null` when
 *  the session carries neither a measured value nor an estimate. */
export function resolveWorkoutCalories(
  session: { calories?: number; caloriesEstimate?: CaloriesEstimate },
  opts: { hcWins: boolean },
): ResolvedWorkoutCalories | null {
  const measured =
    typeof session.calories === "number" ? session.calories : undefined;
  const estimate = session.caloriesEstimate;
  const hasMeasured = measured !== undefined;
  const hasEstimate = estimate !== undefined;

  if (!hasMeasured && !hasEstimate) return null;

  // Only one figure exists → it's canonical, no preference to apply.
  if (!hasMeasured) {
    return { kcal: estimate!.kcal, source: "jl:estimate", estimate };
  }
  if (!hasEstimate) {
    return { kcal: measured!, source: "hc", measured };
  }

  // Both present → the preference decides; the loser is retained as secondary.
  return opts.hcWins
    ? { kcal: measured!, source: "hc", measured, estimate }
    : { kcal: estimate!.kcal, source: "jl:estimate", measured, estimate };
}
